import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { usePracticeDashboard } from "../hooks/usePractice";

const DASHBOARD_NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", icon: "🏠", label: "Home" },
      { to: "/practice", icon: "🧘", label: "Daily Practice" },
      { to: "/mood", icon: "🌙", label: "Mood Tracker" },
      { to: "/dreams", icon: "✨", label: "Dream Journal" },
    ],
  },
  {
    label: "Growth",
    items: [
      { to: "/wisdom", icon: "📚", label: "Wisdom Library" },
      { to: "/badges", icon: "🏅", label: "Badge Vault" },
      { to: "/leaderboard", icon: "🏆", label: "Leaderboard" },
      { to: "/healing", icon: "🔮", label: "Healing" },
    ],
  },
  {
    label: "Stories & Media",
    items: [
      { to: "/blog", icon: "✍️", label: "Blog" },
      { to: "/magazine", icon: "📰", label: "Aekam Magazine" },
      { to: "/podcast", icon: "🎙️", label: "Podcast" },
      { to: "/portfolio", icon: "🖼️", label: "Portfolio" },
    ],
  },
  {
    label: "Community",
    items: [
      { to: "/events", icon: "🗓️", label: "Events" },
      { to: "/volunteer", icon: "🤝", label: "Volunteer" },
      { to: "/shop", icon: "🛍️", label: "Sacred Shop" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/pass", icon: "🎟️", label: "My Pass" },
      { to: "/profile", icon: "👤", label: "My Profile" },
      { to: "/donate", icon: "💛", label: "Support Us" },
      { to: "/contact", icon: "✉️", label: "Contact" },
    ],
  },
];

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good morning", icon: "☀️" };
  if (hour < 17) return { text: "Good afternoon", icon: "🌤️" };
  return { text: "Good evening", icon: "🌙" };
}

function capitalize(value: string | null | undefined) {
  if (!value) return "Free";
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

export function DashboardPage() {
  const { user, profile } = useAuth();
  const { stats, weeklyMinutes, sessions, loading, practiceRecommendations } = usePracticeDashboard();

  const greeting = getGreeting();
  const displayName = profile?.display_name?.trim() || user?.email?.split("@")[0] || "Seeker";
  const tierLabel = `${capitalize(profile?.tier)} Member`;
  const streak = stats?.current_streak ?? 0;
  const totalSessions = stats?.total_sessions ?? 0;
  const totalMinutes = stats?.total_minutes ?? 0;
  const weeklyGoal = 150;
  const weeklyProgress = Math.min(Math.round((weeklyMinutes / weeklyGoal) * 100), 100);
  const latestSession = sessions[0];
  const featuredPractice = practiceRecommendations[0];

  const recommendedActions = [
    {
      icon: featuredPractice?.icon ?? "🧘",
      title: featuredPractice ? featuredPractice.title : streak > 0 ? "Protect your streak" : "Start your first practice",
      text: featuredPractice
        ? featuredPractice.reason
        : streak > 0
          ? `You are on a ${streak}-day streak. Log a short session today.`
          : "A 5-minute session is enough to begin momentum.",
      to: "/practice",
      cta: featuredPractice ? `Start ${featuredPractice.duration_minutes} min` : "Open Practice",
    },
    {
      icon: "📚",
      title: "Read one wisdom piece",
      text: "Pair practice with one short reading to make the dashboard feel complete, not fragmented.",
      to: "/wisdom",
      cta: "Open Wisdom",
    },
    {
      icon: "🗓️",
      title: "Join the next event",
      text: "Events are the strongest bridge between solitary practice and community retention.",
      to: "/events",
      cta: "Browse Events",
    },
  ];

  return (
    <section className="section section--tight dashboard-page">
      <div className="container">
        <div className="dashboard-layout">
          <aside className="dashboard-sidebar">
            <div className="dashboard-profile-card">
              <div className="dashboard-avatar">{displayName[0]?.toUpperCase() ?? "S"}</div>
              <div className="dashboard-profile-card__name">{displayName}</div>
              <div className="dashboard-profile-card__role">{tierLabel}</div>
              <div className="dashboard-profile-card__meta">
                <span>🔥 {streak} day streak</span>
                <span>⏱ {weeklyMinutes} min this week</span>
              </div>
            </div>

            <nav className="dashboard-sidenav" aria-label="Dashboard navigation">
              {DASHBOARD_NAV_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className="dashboard-sidenav__group">{group.label}</div>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/dashboard"}
                      className={({ isActive }) => `dashboard-sidenav__link${isActive ? " active" : ""}`}
                    >
                      <span className="dashboard-sidenav__icon" aria-hidden="true">{item.icon}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>

            <div className="dashboard-quick-stats">
              <div className="dashboard-quick-stats__title">This Week</div>
              <div className="dashboard-quick-stat-grid">
                <div className="dashboard-quick-stat">
                  <span className="dashboard-quick-stat__value">{weeklyMinutes}</span>
                  <span className="dashboard-quick-stat__label">Minutes</span>
                </div>
                <div className="dashboard-quick-stat">
                  <span className="dashboard-quick-stat__value">{streak}</span>
                  <span className="dashboard-quick-stat__label">Streak</span>
                </div>
                <div className="dashboard-quick-stat">
                  <span className="dashboard-quick-stat__value">{weeklyProgress}%</span>
                  <span className="dashboard-quick-stat__label">Goal</span>
                </div>
              </div>
            </div>
          </aside>

          <main className="dashboard-main">
            <div className="dashboard-greeting-wrap">
              <div className="dashboard-greeting">
                <span>{greeting.icon}</span>
                <h1 className="dashboard-greeting__text">{greeting.text}, {displayName}</h1>
              </div>
              <p className="dashboard-greeting__sub">This dashboard should help you decide what to do next in under 10 seconds.</p>
            </div>

            <div className="dashboard-overview-grid">
              <div className="dashboard-overview-card dashboard-overview-card--warm">
                <div className="dashboard-overview-card__label">Current streak</div>
                <div className="dashboard-overview-card__value">{streak}</div>
                <div className="dashboard-overview-card__meta">days of consistency</div>
              </div>
              <div className="dashboard-overview-card">
                <div className="dashboard-overview-card__label">Weekly minutes</div>
                <div className="dashboard-overview-card__value">{weeklyMinutes}</div>
                <div className="dashboard-overview-card__meta">of {weeklyGoal} min goal</div>
              </div>
              <div className="dashboard-overview-card">
                <div className="dashboard-overview-card__label">Sessions logged</div>
                <div className="dashboard-overview-card__value">{totalSessions}</div>
                <div className="dashboard-overview-card__meta">lifetime sessions</div>
              </div>
              <div className="dashboard-overview-card">
                <div className="dashboard-overview-card__label">Total minutes</div>
                <div className="dashboard-overview-card__value">{totalMinutes}</div>
                <div className="dashboard-overview-card__meta">across all practice</div>
              </div>
            </div>

            <div className="dashboard-section-label">Next Best Actions</div>
            <div className="dashboard-action-grid">
              {recommendedActions.map((action) => (
                <div key={action.title} className="card dashboard-action-card">
                  <div className="dashboard-action-card__icon">{action.icon}</div>
                  <div className="dashboard-action-card__title">{action.title}</div>
                  <p className="dashboard-action-card__text">{action.text}</p>
                  <Link to={action.to} className="btn btn--outline btn--sm">{action.cta}</Link>
                </div>
              ))}
            </div>

            <div className="dashboard-section-label" style={{ marginTop: "1.5rem" }}>Focus for Today</div>
            <div className="card dashboard-practice-card">
              <div className="dashboard-practice-card__top">
                <div>
                  <h3>{streak > 0 ? "Keep the rhythm alive" : "Begin your rhythm"}</h3>
                  <p className="dashboard-practice-card__copy">
                    {loading
                      ? "Loading your current practice state..."
                      : latestSession
                        ? `Your last session was ${latestSession.duration_minutes} minutes. One more session today keeps momentum strong.`
                        : "Begin with a short breathing or meditation session and let the dashboard grow with you."}
                  </p>
                </div>
                <span className="dashboard-practice-card__emoji">🧘</span>
              </div>
              <div className="dashboard-progress-inline">
                <div className="dashboard-progress-inline__bar">
                  <div className="dashboard-progress-inline__fill" style={{ width: `${weeklyProgress}%` }} />
                </div>
                <span className="dashboard-progress-inline__value">{weeklyProgress}% weekly goal</span>
              </div>
              <div className="dashboard-practice-card__actions">
                <Link to="/practice" className="btn btn--primary">Open Practice</Link>
                <Link to="/wisdom" className="btn btn--outline">Read Wisdom</Link>
              </div>
            </div>

            <div className="dashboard-section-label" style={{ marginTop: "1.5rem" }}>Recent Activity</div>
            <div className="card">
              {sessions.slice(0, 4).map((session) => (
                <div key={session.id} className="dashboard-activity-row">
                  <span className="dashboard-activity-row__icon">🕉️</span>
                  <div>
                    <p className="dashboard-activity-row__text">
                      Logged a {session.duration_minutes}-minute {session.practice_type} session
                    </p>
                    <p className="dashboard-activity-row__time">
                      {new Date(session.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </p>
                  </div>
                </div>
              ))}
              {!loading && sessions.length === 0 && (
                <div className="dashboard-empty-state">
                  <p>No activity yet. Start a session to make this dashboard meaningful.</p>
                </div>
              )}
            </div>
          </main>

          <aside className="dashboard-widgets">
            <div className="card dashboard-widget">
              <h4 className="dashboard-widget__heading">Recommended for You</h4>
              <div style={{ marginTop: "0.75rem" }}>
                <Link to="/practice" className="dashboard-rec-item">
                  <span className="dashboard-rec-item__icon">🔥</span>
                  <div>
                    <p className="dashboard-rec-item__title">Complete today’s practice</p>
                    <p className="dashboard-rec-item__meta">Highest retention action</p>
                  </div>
                  <span style={{ color: "var(--c-ink-75)", marginLeft: "auto" }}>›</span>
                </Link>
                <Link to="/events" className="dashboard-rec-item">
                  <span className="dashboard-rec-item__icon">🗓️</span>
                  <div>
                    <p className="dashboard-rec-item__title">Join an upcoming gathering</p>
                    <p className="dashboard-rec-item__meta">Build community momentum</p>
                  </div>
                  <span style={{ color: "var(--c-ink-75)", marginLeft: "auto" }}>›</span>
                </Link>
                <Link to="/shop" className="dashboard-rec-item">
                  <span className="dashboard-rec-item__icon">🛍️</span>
                  <div>
                    <p className="dashboard-rec-item__title">Explore sacred tools</p>
                    <p className="dashboard-rec-item__meta">Support your daily ritual</p>
                  </div>
                  <span style={{ color: "var(--c-ink-75)", marginLeft: "auto" }}>›</span>
                </Link>
              </div>
            </div>

            <div className="card dashboard-widget" style={{ marginTop: "1rem" }}>
              <h4 className="dashboard-widget__heading">Member Snapshot</h4>
              <div className="dashboard-member-list">
                <div className="dashboard-member-list__row"><span>Tier</span><strong>{capitalize(profile?.tier)}</strong></div>
                <div className="dashboard-member-list__row"><span>Current platform</span><strong>{profile?.preferences?.last_platform ?? "default"}</strong></div>
                <div className="dashboard-member-list__row"><span>Access</span><strong>{profile?.platform_access?.waytomoksha || profile?.platform_access?.mokshapass ? "Active" : "Limited"}</strong></div>
              </div>
            </div>

            <div className="card dashboard-widget" style={{ marginTop: "1rem" }}>
              <h4 className="dashboard-widget__heading">Quick Links</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.75rem" }}>
                <Link to="/events" className="btn btn--outline btn--sm">Browse Events</Link>
                <Link to="/healing" className="btn btn--outline btn--sm">Book Healing Session</Link>
                <Link to="/donate" className="btn btn--outline btn--sm">Support the Mission</Link>
                <Link to="/profile" className="btn btn--outline btn--sm">Edit Profile</Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}